<template>
	<div class="stage">
		<page-header title="Carriers" />

		<filter-bar
			:show-filters="showFilters"
			:has-export="true"
			:show-add-button="$global.hasUserRole(userRoles, 'carriers:create')"
			add-button-label="Add Carrier"
			@toggle-filters="showFilters = !showFilters"
			@reset-filters="resetSearchParams"
			@export="exportToCsv"
			@add-new="routeTo('/carriers/new')"
		/>

		<filter-section
			:show="showFilters"
			:filters="searchParams"
			:search-term="searchField"
			:tenants="tenants"
			date-label="Date Added"
			show-tenant-filter
			show-date-filter
			show-search-filter
			search-label="Name"
			@update:filters="searchParams = $event"
			@update:search-term="searchField = $event"
			@tenant-change="$global.setSelectedTenant"
		/>

		<data-view
			:view-mode="'table'"
			:columns="columns"
			:sort-column="sortColumn"
			:reverse="reverse"
			:loading="loading"
			:label="'Carriers'"
			:total-items="totalCarriers"
			:page-size="pageSize"
			:current-page="currentPage"
			:pages="pages"
			@sort="setSort"
			@update:page-size="changePageSize"
			@page-change="changePage"
		>
			<template #table-view>
				<generic-table
					:items="carriers"
					:columns="columns"
					item-type="carriers"
					base-route="carriers"
					:empty-state-config="{
						icon: 'fa-light fa-satellite-dish',
						title: 'No carriers yet',
						description: 'Create your first carrier to get started.',
						action: $global.hasUserRole(userRoles, 'carriers:create')
							? {
									label: 'Create Carrier',
									icon: 'fa-light fa-plus',
									handler: () => routeTo('/carriers/new')
								}
							: null
					}"
					:can-edit="$global.hasUserRole(userRoles, 'carriers:update')"
					:view-mode="'table'"
					:actions="[
						{
							handler: (carrier) => routeTo(`/carriers/${carrier.id}`),
							icon: 'fa-light fa-pencil',
							label: 'Edit'
						},
						{
							permission: 'carriers:delete',
							handler: deleteCarrier,
							icon: 'fa-light fa-delete-left',
							label: 'Delete'
						}
					]"
				>
					<template #tenant="{ item }">
						{{ item.tenant?.companyName || 'Unassigned' }}
					</template>
					<template #transport="{ item }">
						{{ item.transport.toUpperCase() }}
					</template>
					<template #requiresAuth="{ item }">
						{{ item.requires_auth ? 'Yes' : 'No' }}
					</template>
					<template #mobile-view="{ item }">
						<div class="flex w-full flex-col">
							<div class="text-md absolute right-4 top-4 block max-w-[160px]">
								<p class="text-right font-semibold uppercase text-gray-400">Tenant</p>
								<p class="truncate text-right">{{ item.tenant?.companyName || 'Unassigned' }}</p>
							</div>
							<div class="flex flex-col gap-2">
								<div class="flex flex-col">
									<p class="font-semibold uppercase text-gray-500">Carrier:</p>
									<p>{{ item.name }}</p>
								</div>
								<div class="flex flex-col">
									<p class="font-semibold uppercase text-gray-500">Server:</p>
									<p>{{ item.server }}</p>
								</div>
								<div class="flex flex-col">
									<p class="font-semibold uppercase text-gray-500">Date added:</p>
									<p>{{ $global.dateFormat(item.createdAt, 'DD/MM/YY') }}</p>
								</div>
							</div>
						</div>
					</template>
				</generic-table>
			</template>
		</data-view>
	</div>
</template>

<script>
import carriersApi from '../scripts/carriers/carriers.api'
import { router } from '../router'
import moment from 'moment'
import axios from 'axios'
import debounce from 'lodash.debounce'

export default {
	inject: ['user', 'userRoles', 'tenants'],
	data() {
		return {
			columns: [
				{
					key: 'name',
					label: 'Name',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-3 col-lg-2'
				},
				{
					key: 'tenant',
					label: 'Tenant',
					class: 'col-lg-2 col-md-3 col-sm-3'
				},
				{
					key: 'server',
					label: 'Server',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2'
				},
				{
					key: 'transport',
					label: 'Transport',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-lg-1 hideTablet'
				},
				{
					key: 'requiresAuth',
					label: 'Requires Auth?',
					class: 'col-xs-12 col-lg-2 hideTabletLandscape'
				},
				{
					key: 'createdAt',
					label: 'Date Added',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2'
				}
			],
			showFilters: true,
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			},
			carriers: [],
			totalCarriers: 0,
			pageSize: 25,
			currentPage: 1,
			sortColumn: 'name',
			reverse: false,
			loading: false,
			searchField: '',
			dates: []
		}
	},

	computed: {
		pages() {
			const numPages = Math.ceil(this.totalCarriers / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		},
		selectedTenantName() {
			if (!this.searchParams.selectedTenant) return 'All Tenants'
			const tenant = this.tenants?.find((t) => t.id === parseInt(this.searchParams.selectedTenant))
			return tenant?.companyName || 'All Tenants'
		}
	},

	watch: {
		dates: {
			handler(val) {
				if (val?.length === 2) {
					this.searchParams.dateAddedFrom = moment(val[0]).format('YYYY-MM-DD')
					this.searchParams.dateAddedTo = moment(val[1]).format('YYYY-MM-DD')
					this.reloadCarriers()
				}
			},
			deep: true
		},

		searchParams: {
			handler() {
				this.reloadCarriers()
			},
			deep: true
		},

		searchField(newValue, oldValue) {
			this.debouncedWatch(newValue, oldValue)
		}
	},

	created() {
		if (this.$global.getSelectedTenant()) {
			this.searchParams.selectedTenant = this.$global.getSelectedTenant()
		}

		this.debouncedWatch = debounce(this.reloadCarriers, 1000)

		if (this.$route.query?.globalSearch) {
			this.reloadCarriers(this.$route.query.globalSearch)
		} else {
			this.reloadCarriers()
		}
	},

	beforeUnmount() {
		this.debouncedWatch.cancel()
	},

	methods: {
		setSort(column, reverse) {
			if (this.sortColumn !== column) {
				this.reverse = false
			} else {
				this.reverse = reverse
			}
			this.sortColumn = column
			this.reloadCarriers()
		},

		changePageSize(size) {
			this.pageSize = size
			const numPages = Math.ceil(this.totalCarriers / size) || 1
			this.currentPage = Math.min(this.currentPage, numPages)
			this.$nextTick(() => {
				this.reloadCarriers()
			})
		},

		changePage(page) {
			this.currentPage = page
			this.reloadCarriers()
		},

		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			}
			this.dates = []
			this.searchField = ''
		},

		getSearchParams() {
			const params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {},
				order: {
					[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
				}
			}

			if (this.searchParams.dateAddedFrom) {
				params.where.createdAtFrom = `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 00:00:00`
				params.where.createdAtTo = this.searchParams.dateAddedTo
					? `${moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD')} 23:59:59`
					: `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 23:59:59`
			}

			if (this.searchParams.selectedTenant) {
				params.where.selectedTenant = this.searchParams.selectedTenant
			}

			if (this.searchField) {
				params.searchTerm = this.searchField
			}

			return params
		},

		async reloadCarriers(globalSearch) {
			try {
				this.loading = true
				let params = this.getSearchParams()

				if (globalSearch) {
					params = { ...params, searchTerm: globalSearch }
				} else if (this.searchField) {
					params.searchTerm = this.searchField
				}

				const response = await carriersApi.getAll(params)
				this.totalCarriers = response.data.count
				this.carriers = response.data.results

				const maxPages = Math.ceil(this.totalCarriers / this.pageSize) || 1
				if (this.currentPage > maxPages) {
					this.currentPage = 1
					this.reloadCarriers(globalSearch)
					return
				}
			} catch (err) {
				console.error(err)
			} finally {
				this.loading = false
			}
		},

		routeTo(routeName) {
			router.push(routeName)
		},

		async exportToCsv() {
			try {
				const searchParams = { ...this.getSearchParams(), exportCsv: true }
				const response = await axios.get(`${window.dtServerUrl}/carriers`, { params: searchParams })

				const blob = new Blob([response.data], { type: response.headers.get('content-type') })
				const link = document.createElement('a')
				link.href = window.URL.createObjectURL(blob)
				link.download = 'teleflow-carriers.csv'
				link.click()
			} catch (error) {
				console.error(error)
			}
		},

		deleteCarrier(carrier) {
			this.$swal({
				title: 'Delete Carrier',
				text: `Would you like to delete carrier '${carrier.name}'?`,
				showCancelButton: true,
				confirmButtonText: 'Delete',
				cancelButtonText: 'Cancel'
			}).then((result) => {
				if (result.isConfirmed) {
					carriersApi
						.delete(carrier.id)
						.then(() => this.reloadCarriers())
						.catch(console.error)
				}
			})
		}
	}
}
</script>
